import { useState, useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';

export function Login() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { state, login, ssoLogin } = useAuth();
  const { t, i18n } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [ssoError, setSsoError] = useState('');

  useEffect(() => {
    const err = searchParams.get('sso_error');
    if (err) {
      setSsoError(t(`login.ssoErrors.${err}`, { defaultValue: t('login.ssoErrors.unknown') }));
      searchParams.delete('sso_error');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  // Probe whether Entra SSO is configured on the backend (disabled locally).
  useEffect(() => {
    fetch('/internal/v1/auth/sso/status')
      .then((r) => (r.ok ? r.json() : { enabled: false }))
      .then((d) => setSsoEnabled(!!d.enabled))
      .catch(() => setSsoEnabled(false));
  }, []);

  // While the session is being restored, render nothing (avoids a login-form flash).
  if (state.status === 'loading') {
    return null;
  }

  // If already authenticated, redirect
  if (state.status === 'authenticated') {
    return <Navigate to="/dashboard" replace />;
  }
  if (state.status === 'totp_verify') {
    return <Navigate to="/2fa" replace />;
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError(t('login.errorBothFields'));
      return;
    }

    setIsLoading(true);
    try {
      await login(email, password);
      // State change in AuthProvider triggers the redirect guards above.
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('login.errorGeneric'));
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSso() {
    if (!ssoEnabled) return;
    // Full-page redirect into the Entra flow; the document unloads, so no
    // client-side navigate is needed (it would be dead code / cause a flash).
    await ssoLogin();
  }

  return (
    <div className="min-h-dvh animated-gradient-bg relative flex">
      {/* Top-left: Unifiber logo */}
      <div className="absolute top-4 left-4 sm:top-8 sm:left-10 z-10">
        <img src="/UnifiberLogoWhite.png" alt="Unifiber" className="h-8 sm:h-12 opacity-90" />
      </div>

      {/* Left side: App name (desktop only) */}
      <div className="hidden lg:flex lg:w-[50%] relative z-10 flex-col justify-center items-start pl-20 pr-12">
        <h1 className="text-white text-7xl font-bold tracking-tight drop-shadow-lg">MDU Invoicing</h1>
      </div>

      {/* Right side: Glass login card */}
      <div className="flex-1 flex items-start sm:items-center justify-center relative z-10 px-6 pt-20 pb-6 sm:py-12">
        <div className="glass-card-login rounded-2xl p-6 sm:p-10 w-full max-w-md space-y-6">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-4">
            <h2 className="text-3xl font-bold gradient-text">MDU Invoicing</h2>
          </div>

          {/* Header */}
          <div>
            <h2 className="text-xl font-bold text-white">{t('login.title')}</h2>
            <p className="text-white/60 mt-1 text-sm">{t('login.subtitle')}</p>
          </div>

          {/* Language selector */}
          <div className="flex items-center gap-1 bg-white/[0.08] rounded-lg p-1 w-fit border border-white/[0.15]">
            {['EN', 'FR', 'NL'].map((l) => (
              <button
                key={l}
                type="button"
                aria-pressed={i18n.language.startsWith(l.toLowerCase())}
                onClick={() => i18n.changeLanguage(l.toLowerCase())}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                  i18n.language.startsWith(l.toLowerCase())
                    ? 'bg-white/20 text-white'
                    : 'text-white/60 hover:text-white/90'
                }`}
              >
                {l}
              </button>
            ))}
          </div>

          {/* Login form */}
          <form onSubmit={handleLogin} className="space-y-4">
            {ssoError && (
              <div className="px-4 py-2.5 rounded-lg bg-amber-500/20 border border-amber-400/30 text-amber-200 text-sm font-medium">
                {ssoError}
              </div>
            )}

            {error && (
              <div className="px-4 py-2.5 rounded-lg bg-red-500/20 border border-red-400/30 text-red-200 text-sm font-medium">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-1.5">
                {t('login.emailLabel')}
              </label>
              <input
                type="email"
                id="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('login.emailPlaceholder')}
                disabled={isLoading}
                className="glass-input w-full px-4 py-2.5 rounded-lg text-sm text-white placeholder:text-white/50 focus:outline-none focus:border-brand-rose focus:bg-white/[0.22] focus:ring-[3px] focus:ring-brand-rose/25 disabled:opacity-50"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="password" className="block text-sm font-medium text-white/80">
                  {t('login.passwordLabel')}
                </label>
                <button
                  type="button"
                  onClick={() => toast.info(t('login.forgotPasswordToast'))}
                  className="text-xs font-medium text-white/60 hover:text-white transition-colors underline underline-offset-2"
                >
                  {t('login.forgotPassword')}
                </button>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('login.passwordPlaceholder')}
                  disabled={isLoading}
                  className="glass-input w-full px-4 py-2.5 rounded-lg text-sm pr-12 text-white placeholder:text-white/50 focus:outline-none focus:border-brand-rose focus:bg-white/[0.22] focus:ring-[3px] focus:ring-brand-rose/25 disabled:opacity-50"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 rounded-lg text-white font-semibold text-sm shadow-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose hover:-translate-y-px hover:shadow-[0_8px_25px_rgba(237,18,95,0.4)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2"
            >
              {isLoading && (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              )}
              {isLoading ? t('login.signingIn') : t('login.signIn')}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-4 text-xs text-white/40 font-medium">
            <div className="flex-1 h-px bg-white/[0.22]" />
            <span>{t('login.or')}</span>
            <div className="flex-1 h-px bg-white/[0.22]" />
          </div>

          {/* Microsoft SSO */}
          <button
            type="button"
            disabled={!ssoEnabled}
            onClick={handleSso}
            title={ssoEnabled ? t('login.microsoftSso') : t('login.ssoComingSoon')}
            className={
              ssoEnabled
                ? 'w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-white/10 border border-white/[0.15] hover:bg-white/15 transition-colors cursor-pointer'
                : 'w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-white/40 bg-white/5 border border-white/[0.15] cursor-not-allowed'
            }
          >
            <svg width="18" height="18" viewBox="0 0 21 21">
              <rect x="1" y="1" width="9" height="9" fill="#f25022" opacity={ssoEnabled ? '1' : '0.4'} />
              <rect x="11" y="1" width="9" height="9" fill="#7fba00" opacity={ssoEnabled ? '1' : '0.4'} />
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef" opacity={ssoEnabled ? '1' : '0.4'} />
              <rect x="11" y="11" width="9" height="9" fill="#ffb900" opacity={ssoEnabled ? '1' : '0.4'} />
            </svg>
            {t('login.microsoftSso')}
            {!ssoEnabled && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 bg-white/10 text-white/50 rounded ml-1">{t('common:badges.soon')}</span>
            )}
          </button>

          {/* Footer */}
          <p className="text-center text-xs text-white/35 pt-2">
            {t('login.noAccount')}
          </p>
        </div>
      </div>

      {/* Bottom copyright */}
      <div className="absolute bottom-6 left-0 right-0 text-center z-10">
        <p className="text-white/25 text-xs">{t('common:footer.copyright', { year: new Date().getFullYear() })}</p>
      </div>
    </div>
  );
}
